export type SemanticEnumValueStatus = "Success" | "Processing" | "Error" | "Default" | "Warning";

export type SemanticEnumTone = "success" | "processing" | "default" | "error" | "warning";

export type SemanticEnumTagColor = string;

export type SemanticEnumValuePresentation<TLegendSemantic extends string = string> = {
  tone: SemanticEnumTone;
  tagColor?: SemanticEnumTagColor;
  valueEnumStatus?: SemanticEnumValueStatus;
  legendSemantic?: TLegendSemantic;
};

export type SemanticEnumRegistry<
  TValue extends string = string,
  TLegendSemantic extends string = string,
> = Record<TValue, SemanticEnumValuePresentation<TLegendSemantic>>;

export type SemanticEnumValueEnumItem = {
  text: string;
  status?: SemanticEnumValueStatus;
};

export type SemanticEnumValueEnumRecord = Record<string, SemanticEnumValueEnumItem>;

export const DEFAULT_TAG_COLOR_BY_TONE: Record<SemanticEnumTone, SemanticEnumTagColor> = {
  success: "success",
  processing: "processing",
  default: "default",
  error: "error",
  warning: "warning",
};

export const DEFAULT_VALUE_ENUM_STATUS_BY_TONE: Record<SemanticEnumTone, SemanticEnumValueStatus> =
  {
    success: "Success",
    processing: "Processing",
    default: "Default",
    error: "Error",
    warning: "Warning",
  };

function normalizeSemanticEnumValue(value?: string | number | null) {
  return String(value ?? "").trim();
}

function getSemanticEnumEntry<TValue extends string, TLegendSemantic extends string>(
  registry: SemanticEnumRegistry<TValue, TLegendSemantic>,
  value?: string | number | null,
) {
  const normalized = normalizeSemanticEnumValue(value);
  if (!normalized) return null;
  return registry[normalized as TValue] ?? null;
}

export function getSemanticEnumPresentation<TValue extends string, TLegendSemantic extends string>(
  registry: SemanticEnumRegistry<TValue, TLegendSemantic>,
  value?: string | number | null,
): SemanticEnumValuePresentation<TLegendSemantic> | null {
  const entry = getSemanticEnumEntry(registry, value);
  if (!entry) return null;
  const legendSemantic = entry.legendSemantic;
  return {
    tone: entry.tone,
    tagColor: entry.tagColor ?? DEFAULT_TAG_COLOR_BY_TONE[entry.tone],
    valueEnumStatus: entry.valueEnumStatus ?? DEFAULT_VALUE_ENUM_STATUS_BY_TONE[entry.tone],
    ...(legendSemantic === undefined ? {} : { legendSemantic }),
  };
}

export function getSemanticEnumTagColor<TValue extends string, TLegendSemantic extends string>(
  registry: SemanticEnumRegistry<TValue, TLegendSemantic>,
  value?: string | number | null,
  fallback: SemanticEnumTagColor = "warning",
) {
  return getSemanticEnumPresentation(registry, value)?.tagColor ?? fallback;
}

export function getSemanticEnumValueStatus<TValue extends string, TLegendSemantic extends string>(
  registry: SemanticEnumRegistry<TValue, TLegendSemantic>,
  value?: string | number | null,
  fallback: SemanticEnumValueStatus = "Warning",
) {
  return getSemanticEnumPresentation(registry, value)?.valueEnumStatus ?? fallback;
}

export function getSemanticEnumLegendSemantic<
  TValue extends string,
  TLegendSemantic extends string,
>(registry: SemanticEnumRegistry<TValue, TLegendSemantic>, value?: string | number | null) {
  return getSemanticEnumPresentation(registry, value)?.legendSemantic ?? null;
}

export function buildSemanticEnumValueEnum<TValue extends string, TLegendSemantic extends string>(
  registry: SemanticEnumRegistry<TValue, TLegendSemantic>,
  values: readonly TValue[],
  getLabel: (value: TValue) => string,
): SemanticEnumValueEnumRecord {
  const valueEnum: SemanticEnumValueEnumRecord = {};
  for (const value of values) {
    valueEnum[value] = {
      text: getLabel(value),
      status: getSemanticEnumValueStatus(registry, value),
    };
  }
  return valueEnum;
}
